import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Trophy, Crown, Share2, ArrowUpRight, ArrowLeft, Medal } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api from '../lib/api';
import { BravxRoleBadges } from '../components/BravxRoleBadges';

const GOLD = '#D4AF37';

export default function BravxLeaderboardPage() {
  const [period, setPeriod] = useState('month'); // 'month' | 'all'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoFellBack, setAutoFellBack] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/bravx/leaderboard?period=${period}&limit=10`);
        const got = res.data || { entries: [] };
        // If the user is viewing the monthly board but there are NO entries this
        // month yet, transparently fall back to the all-time ranking so the page
        // never looks empty when there are real users with points.
        if (
          mounted
          && period === 'month'
          && (!got.entries || got.entries.length === 0)
          && !autoFellBack
        ) {
          const allRes = await api.get('/bravx/leaderboard?period=all&limit=10');
          if (mounted) {
            setData(allRes.data);
            setAutoFellBack(true);
          }
        } else if (mounted) {
          setData(got);
          if (period !== 'month') setAutoFellBack(false);
        }
      } catch (_) {
        if (mounted) setData({ entries: [], label: '' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [period]);

  const handleShare = () => {
    const url = window.location.href;
    const text = `🏆 Top 10 BRAVX en Bravo — ${data?.label || ''}. Mirá quiénes lideran y apoyá a tu @ favorito en cada compra.`;
    if (navigator.share) {
      navigator.share({ title: 'Top BRAVX en Bravo', text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado');
    }
  };

  const entries = data?.entries || [];
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`Top BRAVX${data?.label ? ` · ${data.label}` : ''} · Bravo Productora`}
        description="Ranking de los @usernames con más puntos BRAVX. Apoyá a un Bravx en tu próxima compra."
        image={`${process.env.REACT_APP_BACKEND_URL}/api/og/bravx-top.png`}
        url="/bravx/top"
      />
      <Navbar />

      {/* Halo background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full blur-[180px]" style={{ background: `${GOLD}22` }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-24" data-testid="bravx-leaderboard-page">

        <Link to="/bravx" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition-colors mb-6">
          <ArrowLeft size={13} /> Volver
        </Link>

        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-5"
          >
            <Sparkles size={12} className="text-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Sistema BRAVX</span>
          </motion.div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.9]"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="leaderboard-title"
          >
            Top 10 <span className="text-[#D4AF37]">BRAVX</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-xl mx-auto">
            Los <strong className="text-white">@usernames</strong> que más puntos ganaron — comprando entradas y apoyando a sus referidos. Sumate compartiendo tu @ en tus stories.
          </p>

          {/* Period toggle */}
          <div className="inline-flex mt-6 p-1 rounded-full border border-zinc-800 bg-zinc-950">
            <button
              onClick={() => setPeriod('month')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                period === 'month' ? 'bg-[#D4AF37] text-black' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="leaderboard-period-month"
            >
              Este mes
            </button>
            <button
              onClick={() => setPeriod('all')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                period === 'all' ? 'bg-[#D4AF37] text-black' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="leaderboard-period-all"
            >
              Histórico
            </button>
          </div>
          {data?.label && (
            <p className="mt-3 text-[11px] uppercase tracking-[0.24em] text-zinc-500" data-testid="leaderboard-label">
              {data.label}
            </p>
          )}
          {autoFellBack && period === 'month' && (
            <p className="mt-2 text-[11px] text-[#D4AF37]/80" data-testid="leaderboard-fallback-note">
              Sin movimiento aún este mes — mostramos el histórico.
            </p>
          )}

          <div className="mt-6">
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="leaderboard-share-btn"
            >
              <Share2 size={13} /> Compartir ranking
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-500 text-sm">Cargando ranking…</div>
        ) : entries.length === 0 ? (
          <EmptyState period={period} />
        ) : (
          <>
            {/* Podium */}
            {podium.length > 0 && (
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-10" data-testid="leaderboard-podium">
                <PodiumCard entry={podium[1]} place={2} delay={0.05} />
                <PodiumCard entry={podium[0]} place={1} delay={0.0} highlight />
                <PodiumCard entry={podium[2]} place={3} delay={0.1} />
              </div>
            )}

            {/* Rest */}
            {rest.length > 0 && (
              <div className="space-y-2" data-testid="leaderboard-list">
                {rest.map((it, i) => (
                  <RowItem key={it.username} entry={it} delay={0.05 * i} />
                ))}
              </div>
            )}

            {/* CTA bottom */}
            <div className="mt-12 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/5 to-transparent p-6 sm:p-8 text-center" data-testid="leaderboard-cta">
              <Crown size={28} className="mx-auto mb-3 text-[#D4AF37]" />
              <h3 className="text-xl sm:text-2xl font-black tracking-tight uppercase mb-2" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                ¿Querés aparecer acá?
              </h3>
              <p className="text-sm text-zinc-400 max-w-md mx-auto mb-5">
                Compartí tu @ en Instagram y WhatsApp. Cada vez que alguien lo usa en el checkout, sumás puntos.
              </p>
              <Link
                to="/cuenta?tab=bravx"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="leaderboard-cta-claim"
              >
                <Sparkles size={13} /> Reclamar mi @username
              </Link>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

function PodiumCard({ entry, place, delay = 0, highlight = false }) {
  if (!entry) return <div />;
  const heights = { 1: 'sm:pt-2', 2: 'sm:pt-8', 3: 'sm:pt-12' };
  const icon = place === 1 ? <Crown size={20} /> : <Medal size={18} />;
  const hasHandle = !!entry.username;
  const initial = (entry.username || entry.display_name || '?').slice(0, 1).toUpperCase();
  const cardClasses = `block rounded-2xl border p-4 sm:p-5 text-center transition-all ${hasHandle ? 'hover:-translate-y-1' : 'cursor-default'} ${
    highlight
      ? 'bg-gradient-to-b from-[#D4AF37]/20 to-zinc-900 border-[#D4AF37] shadow-[0_0_40px_rgba(212,175,55,0.18)]'
      : 'bg-zinc-900 border-zinc-800 ' + (hasHandle ? 'hover:border-[#D4AF37]/40' : '')
  }`;
  const Inner = (
    <>
      <div
        className={`mx-auto mb-2 w-7 h-7 rounded-full flex items-center justify-center font-bold ${
          highlight ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-zinc-200'
        }`}
      >
        {icon}
      </div>
      <div className="mb-3">
        {entry.avatar ? (
          <img
            src={entry.avatar}
            alt={entry.username || entry.display_name || ''}
            className={`mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 ${
              highlight ? 'border-[#D4AF37]' : 'border-zinc-700'
            }`}
          />
        ) : (
          <div
            className={`mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-lg sm:text-xl font-black border-2 ${
              highlight ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-zinc-700 bg-zinc-800 text-zinc-300'
            }`}
          >
            {initial}
          </div>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">#{place}</p>
      {hasHandle ? (
        <p className="text-xs sm:text-sm font-bold text-white truncate">@{entry.username}</p>
      ) : (
        <p className="text-xs sm:text-sm font-bold text-white truncate">{entry.display_name || 'Usuario'}</p>
      )}
      {hasHandle && entry.display_name && (
        <p className="text-[10px] text-zinc-500 truncate mt-0.5">{entry.display_name}</p>
      )}
      {!hasHandle && (
        <p className="text-[9px] uppercase tracking-wider text-[#D4AF37]/70 mt-0.5">Sin @ reclamado</p>
      )}
      <BravxRoleBadges entry={entry} className="justify-center mt-1.5" />
      <p
        className={`mt-2 font-black ${highlight ? 'text-[#D4AF37] text-xl sm:text-2xl' : 'text-white text-lg'}`}
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {entry.points}
      </p>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">puntos</p>
    </>
  );
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      className={`${heights[place]}`}
    >
      {hasHandle ? (
        <Link to={`/u/${entry.username}`} className={cardClasses} data-testid={`podium-card-${place}`}>
          {Inner}
        </Link>
      ) : (
        <div className={cardClasses} data-testid={`podium-card-${place}`}>{Inner}</div>
      )}
    </motion.div>
  );
}

function RowItem({ entry, delay = 0 }) {
  const hasHandle = !!entry.username;
  const initial = (entry.username || entry.display_name || '?').slice(0, 1).toUpperCase();
  const rowClasses = `flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 transition-all group ${hasHandle ? 'hover:border-[#D4AF37]/40 hover:bg-zinc-900' : ''}`;
  const Inner = (
    <>
      <div className="w-8 text-center font-black text-zinc-500 group-hover:text-white text-base">
        {entry.rank}
      </div>
      {entry.avatar ? (
        <img src={entry.avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-zinc-700" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-sm font-bold text-zinc-400">
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {hasHandle ? (
          <p className="text-sm font-bold text-white truncate">@{entry.username}</p>
        ) : (
          <p className="text-sm font-bold text-white truncate">{entry.display_name || 'Usuario'}</p>
        )}
        {hasHandle && entry.display_name && (
          <p className="text-xs text-zinc-500 truncate">{entry.display_name}</p>
        )}
        {!hasHandle && (
          <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70">Sin @ reclamado</p>
        )}
        <BravxRoleBadges entry={entry} className="mt-1" />
      </div>
      <div className="text-right">
        <p className="text-base sm:text-lg font-black text-[#D4AF37]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          {entry.points}
        </p>
        <p className="text-[9px] uppercase tracking-wider text-zinc-500">puntos</p>
      </div>
      {hasHandle && <ArrowUpRight size={14} className="text-zinc-600 group-hover:text-[#D4AF37] transition-colors" />}
    </>
  );
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay }}
    >
      {hasHandle ? (
        <Link to={`/u/${entry.username}`} className={rowClasses} data-testid={`leaderboard-row-${entry.rank}`}>
          {Inner}
        </Link>
      ) : (
        <div className={rowClasses} data-testid={`leaderboard-row-${entry.rank}`}>{Inner}</div>
      )}
    </motion.div>
  );
}

function EmptyState({ period }) {
  return (
    <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-zinc-800" data-testid="leaderboard-empty">
      <Trophy size={36} className="mx-auto mb-4 text-zinc-700" />
      <h3 className="text-lg font-bold mb-2">Aún no hay ranking</h3>
      <p className="text-sm text-zinc-500 max-w-md mx-auto">
        {period === 'month'
          ? 'Todavía nadie sumó puntos este mes. ¡Podés ser el primero!'
          : 'El ranking se llena cuando los usuarios apoyan a un @ en el checkout.'}
      </p>
      <Link
        to="/cuenta?tab=bravx"
        className="inline-flex items-center gap-2 mt-5 px-5 py-2 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black text-xs font-bold uppercase tracking-wider"
      >
        <Sparkles size={13} /> Conseguí tu @
      </Link>
    </div>
  );
}
